import React, {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {Box, Typography, Tooltip, Alert, TextField, InputAdornment, Switch, Checkbox, Chip} from "@mui/material";
import {PrimaryBigButton, SecondaryBigButton} from "../../components/buttons/mainButton";
import {Loader} from "../../components/loader/loader";
import {INPUT_TYPES} from "../../patterns/form/formConstants";
import ElementInputWithLabel from "../../patterns/form/elementInputWithLabel";
import {TEmailTemplate, TEmailTemplateComponentType, TEmailTemplateType} from "../../interfaces/email";
import {createEmailTemplate, updateEmailTemplate} from "../../services/AdminService";
import {errorManager, Violation} from "../../common/methods/ApiService";
import {AppContext} from "../../App";
import {getOperation} from "../../services/OperationService";
import {StoreContext} from "../../common/struct/store";
import {getAllParticipantAttributes} from "../../services/ParticipantService";
import EmailTemplateComponentBlock, {LocalComponent, buildVariableMenus} from "./EmailTemplateComponentBlock";
import {toComponentPayloads} from "./componentPayload";
import {useEmailPreview} from "./useEmailPreview";
import EmailTemplatePreview from "./EmailTemplatePreview";
import WyziwygEditor from "../../patterns/wysiwyg/wysiwygEditor";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EmailTemplateAddBlock from "./EmailTemplateAddBlock";

type EmailTemplateFormPanelProps = {
  template: TEmailTemplate | null;
  initialStep?: 1 | 2;
  setClose: () => void;
  setReload: (reload: boolean) => void;
  addSuccess: (success: string) => void;
  addError: (error: string) => void;
  setCancelDialogSave: React.Dispatch<React.SetStateAction<(() => Promise<void>) | null>>;
  setCancelDialogValid: (valid: boolean) => void;
  setOpenCancelDialog: (open: boolean) => void;
};

export default function EmailTemplateFormPanel(props: EmailTemplateFormPanelProps): JSX.Element {
  const {t} = useTranslation();

  const {operationId} = useParams<string>();
  const {template, initialStep, setClose, setReload, addSuccess, addError, setCancelDialogSave, setCancelDialogValid, setOpenCancelDialog} = props;

  const STORE = useContext<StoreContext>(AppContext);
  const isInvitation = template?.templateType === TEmailTemplateType.PARTICIPANT_INVITATION;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingAttributes, setLoadingAttributes] = useState(true);
  const [violations, setViolations] = useState<Violation[]>([]);
  const [name, setName] = useState(template?.name ?? "");
  const [subject, setSubject] = useState(template?.subject ?? "");
  const [buttonLabel, setButtonLabel] = useState(template?.extraVariables?.buttonLabel ?? "");
  const [mailSender, setMailSender] = useState(template?.mailSender ?? "");
  const [shopUrl, setShopUrl] = useState<string | undefined>(undefined);
  const [primaryColor, setPrimaryColor] = useState<string>("#6725D2");
  const [operationLoaded, setOperationLoaded] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const [components, setComponents] = useState<LocalComponent[]>((template?.components ?? []).map(c => ({...c, localId: c.id})));
  const [invitationContent, setInvitationContent] = useState<string | null>(template?.extraVariables?.content ?? null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const mainEditorRef = useRef<any>(null);
  const [participantAttributes, setParticipantAttributes] = useState<any[]>([]);
  const [step, setStep] = useState<1 | 2>(initialStep ?? 1);

  const formIsValid = name.trim() !== "" && subject.trim() !== "";
  const submitRef = useRef<() => Promise<void>>(); useEffect(() => {
    setCancelDialogSave(() => () => submitRef.current!());
    setCancelDialogValid(formIsValid);
  }, [formIsValid]);


  const CONDITIONS = [
    {key: "newPointAdded", label: t("operations.mailing.customisation.condition.newPoints"), desc: t("email_templates.config.trigger.newPoints_desc")},
    {key: "newParticipantAdded", label: t("operations.mailing.customisation.condition.newParticipant"), desc: t("email_templates.config.trigger.newParticipant_desc")},
    {key: "beginOperationAdded", label: t("operations.mailing.customisation.condition.newOperation"), desc: t("email_templates.config.trigger.newOperation_desc")},
  ] as const;
  type ConditionKey = typeof CONDITIONS[number]["key"];

  const [selectedConditions, setSelectedConditions] = useState<Set<ConditionKey>>(
    () => new Set(CONDITIONS.filter(c => template?.[c.key]).map(c => c.key))
  );
  const [recurrenceEnabled, setRecurrenceEnabled] = useState<boolean>((template?.periodicity ?? 0) > 0);

  const toggleCondition = (key: ConditionKey): void => {
    setSelectedConditions(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const conditionFlags: Record<ConditionKey, boolean> = CONDITIONS.reduce((acc, c) => {
    acc[c.key] = selectedConditions.has(c.key);
    return acc;
  }, {} as Record<ConditionKey, boolean>);

  useEffect(() => {
    if (initialStep !== undefined) {
      setStep(initialStep);
    }
  }, [initialStep]);

  const {previewHtml, status} = useEmailPreview({
    template, operationId, components,
    contentOverride: isInvitation ? invitationContent : undefined,
    buttonLabel: isInvitation ? buttonLabel : undefined,
    primaryColor, shopUrl, logoUrl
  });

  useEffect(() => {
    getAllParticipantAttributes()
      .then(setParticipantAttributes)
      .finally(() => setLoadingAttributes(false));
  }, []);

  useEffect(() => {
    if (operationId) {
      getOperation(operationId)
        .then(op => {
          setShopUrl(op.hostname ? `https://${op.hostname}` : undefined);
          setPrimaryColor(op.primaryColor ?? "#6725D2");
          setLogoUrl(op.logoUrl);
        })
        .catch(() => setShopUrl(undefined))
        .finally(() => setOperationLoaded(true));
    }
  }, [operationId]);

  const handleAddComponent = (type: TEmailTemplateComponentType): void => {
    const newId = crypto.randomUUID();
    const defaults = type === TEmailTemplateComponentType.BUTTON ? {label: "Me connecter"} : {};
    setComponents(prev => [...prev, {localId: newId, order: prev.length, type, ...defaults}]);
    setExpandedIds(prev => new Set(prev).add(newId));
  };

  const handleMoveUp = (index: number): void => {
    setComponents(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((c, i) => ({...c, order: i}));
    });
  };

  const handleMoveDown = (index: number): void => {
    setComponents(prev => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((c, i) => ({...c, order: i}));
    });
  };

  const handleDeleteComponent = (localId: string): void => {
    setComponents(prev => prev.filter(c => c.localId !== localId).map((c, i) => ({...c, order: i})));
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.delete(localId);
      return next;
    });
  };

  const handleDuplicateComponent = (localId: string): void => {
    const newId = crypto.randomUUID();
    setComponents(prev => {
      const index = prev.findIndex(c => c.localId === localId);
      if (index === -1) return prev;
      const copy: LocalComponent = {...prev[index], localId: newId, id: undefined};
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next.map((c, i) => ({...c, order: i}));
    });
    setExpandedIds(prev => new Set(prev).add(newId));
  };

  const submit = async (): Promise<void> => {
    if (!operationId) return;

    const newViolations = validateForm();
    if (newViolations.length > 0) {
      setViolations(newViolations);
      setStep(1);
      return;
    }

    const payload = {
      templateType: template?.templateType ?? TEmailTemplateType.CUSTOM_TEMPLATE,
      operation: "bo/operations/" + operationId,
      name,
      subject: subject || null,
      mailSender: mailSender || null,
      periodicity: recurrenceEnabled ? periodicity : 0,
      extraVariables: isInvitation
        ? {content: invitationContent || null, buttonLabel: buttonLabel || null}
        : {},
      ...(isInvitation ? {mailjetId: template?.mailjetId ?? 0} : {}),
      ...conditionFlags,
      components: toComponentPayloads(components),
    };

    setLoading(true);
    setViolations([]);

    try {
      await (template?.id
        ? updateEmailTemplate(template.id, payload)
        : createEmailTemplate(payload)
      );
      addSuccess(t(template?.id
        ? "operation_email_templates.actions.update_success"
        : "operation_email_templates.actions.create_success"
      ));
      setReload(true);
      setClose();
    } catch (error) {
      const result = errorManager(error, t, STORE, navigate);
      if (typeof result === "string") {
        addError(result);
      } else {
        setViolations(result);
        if (hasStep1Violation(result)) setStep(1);
      }
    } finally {
      setLoading(false);
    }
  };
  submitRef.current = submit;

  const [periodicity, setPeriodicity] = useState<number>(template?.periodicity && template.periodicity > 0 ? template.periodicity : 7);
  const validateForm = (): Violation[] => {
    const newViolations: Violation[] = [];

    if (name.trim() === "") {
      newViolations.push({
        field: "name",
        error: t("operations.mailing.customisation.validation.name_required")
      });
    }

    if (subject.trim() === "") {
      newViolations.push({
        field: "subject",
        error: t("operations.mailing.customisation.validation.objet_email_required")
      });
    }
    return newViolations;
  };

  const STEP_1_FIELDS = ["name", "mailSender", "subject", "buttonLabel", "content"];
  const hasStep1Violation = (list: Violation[]): boolean =>
    list.some(v => STEP_1_FIELDS.includes(v.field));

  const verifyBeforeNextStep = (): void => {
    const newViolations = validateForm();
    setViolations(newViolations);

    if (newViolations.length > 0) {
      setStep(1);
      return;
    }
    setStep(2);
  };

  const componentsSection = (
    <Box sx={{mb: 3}}>
      <Typography sx={{mb: 1, fontWeight: "bold"}}>
        {t(isInvitation
          ? "operations.mailing.customisation.components.title_invitation"
          : "operations.mailing.customisation.components.title")} :
      </Typography>

      {components.length === 0 && (
        <Alert severity="info">
          {t(isInvitation
            ? "operations.mailing.customisation.components.empty_invitation"
            : "operations.mailing.customisation.components.empty")}
        </Alert>
      )}

      {components.map((comp, index) => (
        <EmailTemplateComponentBlock
          key={comp.localId}
          component={comp}
          index={index}
          total={components.length}
          expanded={expandedIds.has(comp.localId)}
          participantAttributes={participantAttributes}
          operationId={operationId!}
          primaryColor={primaryColor}
          shopUrl={shopUrl}
          operationLogoUrl={logoUrl}
          onToggle={(): void => setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(comp.localId)
              ? next.delete(comp.localId)
              : next.add(comp.localId);
            return next;
          })}
          onMoveUp={(): void => handleMoveUp(index)}
          onMoveDown={(): void => handleMoveDown(index)}
          onDelete={(): void => handleDeleteComponent(comp.localId)}
          onDuplicate={(): void => handleDuplicateComponent(comp.localId)}
          onChange={(patch): void =>
            setComponents(prev =>
              prev.map(c =>
                c.localId === comp.localId ? {...c, ...patch} : c
              )
            )
          }
          addError={addError}
        />
      ))}
    </Box>
  );

  const stepCustomisation = (
    <Box sx={{display: "flex", flex: 1, minHeight: 0}}>
      <EmailTemplateAddBlock onAdd={handleAddComponent} />

      <Box sx={{flex: 2, borderRight: "1px solid", borderColor: "ornament.dark", display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0}}>
        {/* Zone scrollable — fond gris */}
        <Box sx={{flex: 1, overflowY: "auto", minHeight: 0, p: 3, backgroundColor: "ornament.main"}}>
          <ElementInputWithLabel name="name" value={name}
            label={`${t("operations.mailing.customisation.form.name")} :`}
            helper={t("operations.mailing.customisation.helper.name")}
            helperAsTooltip
            type={INPUT_TYPES.DEFAULT}
            violations={violations}
            updateValue={setName}
            disabled={loading}
          />

          <ElementInputWithLabel
            name="mailSender"
            value={mailSender}
            label={`${t("operations.mailing.customisation.form.mail_sender")} :`}
            type={INPUT_TYPES.DEFAULT}
            violations={violations}
            updateValue={setMailSender}
            disabled={loading}
          />

          <ElementInputWithLabel
            name="subject"
            value={subject}
            label={`${t("operations.mailing.customisation.form.subject")} :`}
            helper={t("operations.mailing.customisation.helper.subject")}
            helperAsTooltip
            type={INPUT_TYPES.DEFAULT}
            violations={violations}
            updateValue={setSubject}
            disabled={loading}
          />

          {isInvitation && (
            <>
              <Box sx={{mb: 3}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                  <Typography variant="body2" sx={{fontWeight: "bold"}}>
                    {t("operations.mailing.customisation.form.content")} :
                  </Typography>
                  <Tooltip title={t("operations.mailing.customisation.helper.content")} arrow>
                    <InfoOutlinedIcon sx={{ml: 1, fontSize: 16}} />
                  </Tooltip>
                </Box>
                <WyziwygEditor
                  bodyTextAlign="center"
                  editorRef={mainEditorRef}
                  operationId={operationId!}
                  initialValue={template?.extraVariables?.content ?? ""}
                  setError={addError}
                  height={400}
                  customMenus={buildVariableMenus(participantAttributes, mainEditorRef, t)}
                  primaryColor={primaryColor}
                  onChange={setInvitationContent}
                />
              </Box>

              {componentsSection}

              <ElementInputWithLabel
                name="buttonLabel"
                value={buttonLabel}
                label={t("operations.mailing.customisation.form.button_label")}
                helper={t("operations.mailing.customisation.helper.button_label")}
                helperAsTooltip
                type={INPUT_TYPES.DEFAULT}
                violations={violations}
                updateValue={setButtonLabel}
                disabled={loading}
              />
            </>
          )}

          {!isInvitation && componentsSection}
        </Box>

        <Box>

          {/* ✅ ALERT (barre au-dessus) */}
          {violations.length > 0 && (
            <Box sx={{px: 2, pb: 1, backgroundColor: "white"}}>
              <Alert severity="error">
                {violations.map((v, i) => (
                  <div key={i}>{v.error}</div>
                ))}
              </Alert>
            </Box>
          )}

          {/* ✅ BARRE DE BOUTONS */}
          <Box sx={{display: "flex", justifyContent: "space-between", gap: 2, p: 2, borderTop: "1px solid", borderColor: "ornament.dark", backgroundColor: "white"}}>
            <SecondaryBigButton label={t("navigation.cancel")} action={(): void => { setViolations(validateForm()); setOpenCancelDialog(true) }} sx={{flex: 1}} />
            <PrimaryBigButton label={t("wizard.next")} action={verifyBeforeNextStep} sx={{flex: 1}} />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const PRESETS = [3, 7, 14, 30];
  const recurrenceRecap = recurrenceEnabled && periodicity > 0
    ? t("email_templates.periodicity.recap", {count: periodicity})
    : null;
  const conditionRecap = CONDITIONS.filter(c => selectedConditions.has(c.key)).map(c => c.label);

  const stepConfig = (
    <Box sx={{display: "flex", flex: 1, flexDirection: "column", minHeight: 0}}>
      <Box sx={{flex: 1, overflowY: "auto", p: 3, minHeight: 0, display: "flex", flexDirection: "column", gap: 3}}>

        <Box>
          <Typography variant="h6">{t("email_templates.config.when_title")}</Typography>
          <Typography variant="body2" sx={{color: "text.secondary"}}>
            {t("email_templates.config.when_subtitle")}
          </Typography>
        </Box>

        {/* Card 1 — Envoi récurrent */}
        <Box sx={{border: "1px solid", borderColor: recurrenceEnabled ? "primary.main" : "ornament.dark", borderRadius: 2, p: 3, backgroundColor: "white", transition: "border-color 150ms"}}>
          <Box sx={{display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontWeight: "bold"}}>{t("email_templates.config.recurring_title")}</Typography>
              <Typography variant="body2" sx={{color: "text.secondary"}}>
                {t("email_templates.config.recurring_desc")}
              </Typography>
            </Box>
            <Switch
              sx={{
                "& .MuiSwitch-switchBase:not(.Mui-checked)": {
                  color: "grey.500",
                },
                "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
                  bgcolor: "grey.400",
                  opacity: 1,
                },
              }}
              checked={recurrenceEnabled} onChange={(e): void => setRecurrenceEnabled(e.target.checked)} />
          </Box>

          {recurrenceEnabled && (
            <Box sx={{mt: 3, display: "flex", flexDirection: "column", gap: 2}}>
              <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                {PRESETS.map(days => (
                  <Chip
                    key={days}
                    label={t("email_templates.periodicity.preset", {count: days})}
                    clickable
                    color={periodicity === days ? "primary" : "default"}
                    variant={periodicity === days ? "filled" : "outlined"}
                    onClick={(): void => setPeriodicity(days)}
                  />
                ))}
              </Box>
              <TextField
                type="number"
                size="small"
                value={periodicity}
                onChange={(e): void => {
                  const v = Number(e.target.value);
                  setPeriodicity(v >= 1 ? v : 1);
                }}
                sx={{maxWidth: 220}}
                InputProps={{endAdornment: (<InputAdornment position="end">{t("email_templates.periodicity.unit")}</InputAdornment>)}}
              />
            </Box>
          )}
        </Box>

        {/* Card 2 — Déclencheurs événementiels */}
        <Box sx={{border: "1px solid", borderColor: selectedConditions.size > 0 ? "primary.main" : "ornament.dark", borderRadius: 2, p: 3, backgroundColor: "white", transition: "border-color 150ms"}}>
          <Typography sx={{fontWeight: "bold"}}>{t("email_templates.config.triggers_title")}</Typography>
          <Typography variant="body2" sx={{color: "text.secondary", mb: 2}}>
            {t("email_templates.config.triggers_desc")}
          </Typography>

          <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
            {CONDITIONS.map(c => {
              const checked = selectedConditions.has(c.key);
              return (
                <Box
                  key={c.key}
                  onClick={(): void => toggleCondition(c.key)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1, p: 2, cursor: "pointer",
                    border: "1px solid", borderColor: checked ? "primary.main" : "ornament.dark", borderRadius: 1,
                    backgroundColor: checked ? "primaryLight.light" : "transparent",
                    transition: "border-color 150ms, background-color 150ms",
                  }}
                >
                  <Checkbox checked={checked} sx={{p: 0}} />
                  <Box sx={{minWidth: 0}}>
                    <Typography variant="body2" sx={{fontWeight: "bold"}}>{c.label}</Typography>
                    <Typography variant="caption" sx={{color: "text.secondary"}}>{c.desc}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Récap */}
        <Box sx={{display: "flex", alignItems: "flex-start", gap: 1, p: 2, borderRadius: 2, backgroundColor: "ornament.light", border: "1px solid", borderColor: "ornament.dark"}}>
          <MailOutlineIcon sx={{color: "primary.main"}} />
          <Box sx={{minWidth: 0}}>
            <Typography variant="body2" sx={{fontWeight: "bold"}}>
              {t("email_templates.config.recap_title")}
            </Typography>
            {!recurrenceRecap && conditionRecap.length === 0
              ? <Typography variant="body2" sx={{color: "text.secondary"}}>{t("email_templates.config.recap_empty")}</Typography>
              : <Box component="ul" sx={{m: 0, pl: 2}}>
                {recurrenceRecap && <Typography component="li" variant="body2">{recurrenceRecap}</Typography>}
                {conditionRecap.map(label => (
                  <Typography component="li" variant="body2" key={label}>{t("email_templates.config.recap_trigger", {label})}</Typography>
                ))}
              </Box>
            }
          </Box>
        </Box>

      </Box>
      <Box sx={{flexShrink: 0, backgroundColor: "white"}}>
        {violations.length > 0 && (
          <Alert severity="error" sx={{mb: 2, animation: "shake 0.2s"}}>
            {violations.map((v, i) => (<div key={i}>{v.error}</div>))}
          </Alert>
        )}

        <Box sx={{display: "flex", justifyContent: "space-between", p: 2, gap: 2, borderTop: "1px solid", borderColor: "ornament.dark", backgroundColor: "white"}}>
          <SecondaryBigButton label={t("wizard.back")} action={(): void => setStep(1)} sx={{flex: 1}} />
          <PrimaryBigButton label={t("wizard.validate")} action={submit} loading={loading} sx={{flex: 1}} />
        </Box>
      </Box>
    </Box>
  );

  if (loadingAttributes || !operationLoaded) { return <Loader height="100%" />; }

  return (
    <Box sx={{display: "flex", flexDirection: "column", flex: 1, minHeight: 0}}>
      {/* NAVIGATION */}
      <Box sx={{display: "flex", borderBottom: "1px solid", borderColor: "ornament.dark"}}>

        <Box
          sx={{p: 2, cursor: "pointer", color: step === 1 ? "primary.main" : "text.primary", borderBottom: step === 1 ? "3px solid" : "none", borderColor: "primary.main"}}
          onClick={(): void => setStep(1)}
        >
          <Typography sx={{fontWeight: step === 1 ? "bold" : 400}}>
            {t("email_templates.steps.custom")}
          </Typography>

        </Box>

        <Box sx={{p: 2, cursor: "pointer", color: step === 2 ? "primary.main" : "text.primary", borderBottom: step === 2 ? "3px solid" : "none", borderColor: "primary.main"}}
          onClick={verifyBeforeNextStep}>
          <Typography sx={{fontWeight: step === 2 ? "bold" : 400}}>
            {t("email_templates.steps.config")}
          </Typography>
        </Box>

      </Box>

      {/* CONTENU */}
      <Box sx={{flex: 1, display: "flex", minHeight: 0}}>

        {/* STEP */}
        <Box sx={{flex: 3, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0}}>
          <Box sx={{flex: 1, minHeight: 0, flexDirection: "column", display: step === 1 ? "flex" : "none"}}>
            {stepCustomisation}
          </Box>

          <Box sx={{flex: 1, minHeight: 0, flexDirection: "column", display: step === 2 ? "flex" : "none"}}>
            {stepConfig}
          </Box>
        </Box>

        {/* PREVIEW uniquement step config */}
        <EmailTemplatePreview
          previewHtml={previewHtml}
          status={status}
          mailSender={mailSender}
          subject={subject}
          templateName={template?.name}
        />


      </Box>

    </Box>
  );
}
